export type CommunityVisibility = 'Public' | 'Private' | 'Unlisted';
// Standard, Question, Poll are the active composer types; legacy types kept for reading existing posts
export type CommunityPostType = 'Standard' | 'EventShare' | 'EventTag' | 'Question' | 'Poll' | 'Resource';
export type CommunityNewPostType = 'Standard' | 'Question' | 'Poll';
export type CommunityReactionType = 'Like' | 'Love' | 'Celebrate' | 'Insightful' | 'Support' | 'Laughing';
export type CommunityAttachEventScope = 'my' | 'going' | 'search';

export interface IAttachableCommunityEvent {
    id: string;
    title: string;
    startDate: string;
    locationLabel?: string;
    attendeeCount: number;
    status: string;
}

export interface ILinkPreview {
    url: string;
    title?: string;
    description?: string;
    imageUrl?: string;
}

export interface ICommunity {
    id: string;
    ownerCompanyId: string;
    name: string;
    slug: string;
    description?: string;
    imageId?: string;
    imageUrl?: string;
    logoUrl?: string;
    imageFocalPointX: number; // 0-100 percentage
    imageFocalPointY: number; // 0-100 percentage
    visibility: CommunityVisibility;
    allowAttendeePosts: boolean;
    isArchived: boolean;
    canManageSettings?: boolean;
    currentUserRole?: string | null;
    createdByUserId: string;
    memberCount: number;
    channelCount?: number;
    joinedAtUtc?: string;
    postCount?: number;
    recentPostCount?: number;
    createdAt: string;
    updatedAt?: string;
    isCollaborator?: boolean;
    isMember?: boolean;
    isMuted?: boolean;
    isJoinable?: boolean;
    ownerCompanyName?: string;
    ownerCompanyKey?: string;
    ownerCompanyImageUrl?: string;
    ownerCompanyPrimaryColour?: string;
    ownerCompanySecondaryColour?: string;
    ownerCompanyEventCount?: number;
}

export interface ICommunityDirectoryMember {
    userId: string;
    displayName: string;
    imageUrl?: string;
    role: string;
    eventCount: number;
    allowConnections: boolean;
    connectionStatus?: 'none' | 'pending' | 'connected' | null;
}

export interface ICommunityComment {
    id: string;
    postId: string;
    authorUserId: string;
    authorName: string;
    authorImageUrl?: string;
    body: string;
    status: string;
    createdAt: string;
    reactions?: IReactionSummary | null;
}

export interface ICreateCommentResponse {
    comment: ICommunityComment;
    commentsCount: number;
}

export type CommunityChannelAccessMode = 'Open' | 'InviteOnly' | 'RequestToJoin';

export interface IChannelMember {
    userId: string;
    displayName: string;
    email?: string;
    companyName?: string;
    imageUrl?: string;
    status: string;
    isInherited?: boolean;
    message?: string;
    createdAt: string;
}

export interface ICommunityChannel {
    id: string;
    communityId: string;
    name: string;
    slug: string;
    isDefault: boolean;
    sortOrder: number;
    accessMode?: CommunityChannelAccessMode;
    canAccess?: boolean;
    canManage?: boolean;
    membershipStatus?: string;
    isMuted?: boolean;
    memberCount?: number;
    pendingRequestCount?: number;
    postingPolicy: 'OwnersAdminsOnly' | 'MembersAllowed';
    isArchived: boolean;
    logoUrl?: string;
    postCount: number;
    createdAt: string;
}

export interface IReactionSummary {
    counts: Record<string, number>;
    userReaction?: CommunityReactionType;
    users?: IReactionUser[];
    totalUsers?: number;
    page?: number;
    pageSize?: number;
}

export interface IReactionUser {
    userId: string;
    displayName: string;
    imageUrl?: string;
    reactionType: CommunityReactionType;
}

export interface ICommunityFeedSummary {
    recentPostsCount: number;
    openQuestionsCount: number;
    livePollsCount: number;
}

export interface ICommunityFeedResponse {
    posts: ICommunityPost[];
    totalCount: number;
    summary: ICommunityFeedSummary;
}

export interface IPostImage {
    id: string;
    url: string;
    displayOrder: number;
}

export interface IStagedImageResult {
    fileName: string;
    success: boolean;
    stagedUrl?: string;
    error?: string;
}

export interface ICommunityPost {
    id: string;
    communityId: string;
    communityName?: string;
    communitySlug?: string;
    communityLogoUrl?: string;
    channelId: string;
    channelName?: string;
    channelLogoUrl?: string;
    // Owner organiser's brand colours — used client-side for the gradient shown when a community/channel has no logo.
    ownerCompanyPrimaryColour?: string;
    ownerCompanySecondaryColour?: string;
    authorUserId: string;
    authorName: string;
    authorImageUrl?: string;
    postType: CommunityPostType;
    title?: string;
    body: string;
    attachedEventId?: string;
    attachedEventUrl?: string;
    metadataJson?: string;
    imageId?: string;
    imageUrl?: string;
    images?: IPostImage[];
    isPinned: boolean;
    isLocked: boolean;
    status: string;
    createdAt: string;
    commentCount: number;
    comments: ICommunityComment[];
    reactions?: IReactionSummary;
    // False when the viewer lacks access to the attached private/invite-only event; metadataJson is redacted server-side in this case.
    hasEventAccess?: boolean;
}

export interface ICreateCommunityRequest {
    name: string;
    description?: string;
    visibility: CommunityVisibility;
    allowAttendeePosts?: boolean;
}

export interface IUpdateCommunityRequest {
    name: string;
    description?: string;
    visibility: CommunityVisibility;
    allowAttendeePosts?: boolean;
    imageFocalPointX?: number;
    imageFocalPointY?: number;
}

export interface ICreateCommunityPostRequest {
    title?: string;
    body: string;
    postType?: CommunityPostType;
    channelId?: string;
    attachedEventId?: string;
    attachedEventUrl?: string;
    metadataJson?: string;
    stagedImageUrl?: string;
    stagedImageUrls?: string[];
}

export interface ICreateCommunityChannelRequest {
    accessMode?: CommunityChannelAccessMode;
    name: string;
    postingPolicy?: 'OwnersAdminsOnly' | 'MembersAllowed';
}

export interface ICreateCommunityCommentRequest {
    body: string;
}

export interface IInviteMemberRequest {
    userId: string;
    role: 'Admin' | 'Moderator' | 'Member' | 'Guest';
}

export interface IInviteExternalCompanyRequest {
    externalCompanyId: string;
    role: 'Admin' | 'Moderator' | 'Member' | 'Guest';
}

// ─── Personal / Unified Feed ──────────────────────────────────────────────────

export interface IUserPost {
    id: string;
    authorUserId: string;
    authorName: string;
    authorImageUrl?: string;
    postType: CommunityNewPostType;
    title?: string;
    body: string;
    metadataJson?: string;
    imageId?: string;
    imageUrl?: string;
    images?: IPostImage[];
    status: string;
    createdAt: string;
    commentCount?: number;
    reactions?: IReactionSummary;
    scope?: string;
    pod_id?: string;
    pod_name?: string;
    pod_gradient?: string;
}

export interface ICreateUserPostRequest {
    body: string;
    postType?: CommunityNewPostType;
    title?: string;
    metadataJson?: string;
    imageUrl?: string;
    stagedImageUrls?: string[];
}

export enum HomeFeedItemTypeValue {
    Event = 0,
    CommunityPost = 1,
    UserPost = 2,
}

export type HomeFeedItemType = 'Event' | 'CommunityPost' | 'UserPost';

export interface IEventFeedItem {
    id: string;
    title: string;
    description: string;
    imageUrl?: string;
    startDate: string;
    endDate: string;
    locationCity: string[];
    locationNames: string[];
    price: number;
    isFavourite: boolean;
    isRegistered: boolean;
    isWaitlisted: boolean;
    isEventPublic?: boolean;
    isInviteOnly?: boolean;
    categoryName?: string;
    tags: string[];
    reactions?: IReactionSummary | null;
}

export interface IHomeFeedItem {
    itemType: HomeFeedItemType;
    timestamp: string;
    event?: IEventFeedItem;
    post?: ICommunityPost;
    userPost?: IUserPost;
}

export interface IHomeFeedCountsMetadata {
    total: number;
    personal: number;
    communities: number;
}

export interface IHomeFeedPagedResponse {
    items: IHomeFeedItem[];
    totalCount: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
    counts?: IHomeFeedCountsMetadata;
}

export interface ICommunityMember {
    id: string;
    userId: string;
    displayName: string;
    imageUrl?: string;
    role: string;
    status: string;
    joinedAtUtc?: string;
}

export interface IReportContentRequest {
    contentType: 'Post' | 'Comment' | 'User';
    contentId: string;
    reason: string;
}

export interface IApiMessageResponse {
    message?: string;
}

export interface IModerationCaseResponse extends IApiMessageResponse {
    moderationCase?: IModerationCase;
    report?: IModerationCase;
}

export interface IModerationCase {
    id: string;
    communityId: string;
    reportedByUserId: string;
    contentType: 'Post' | 'Comment' | 'User';
    contentId: string;
    reason: string;
    status: 'Open' | 'InReview' | 'Actioned' | 'Closed';
    actionTaken?: string;
    actionedByUserId?: string;
    actionedByUserName?: string;
    createdAt: string;
}

export interface IActionModerationCaseRequest {
    action: 'Hide' | 'Remove' | 'Mute' | 'Ban' | 'Lock' | 'NoAction' | 'InReview';
    notes?: string;
}

export interface IModerationCaseDetail {
    case: IModerationCase;
    allowedActions: Array<IActionModerationCaseRequest['action'] | 'Close'>;
    targetAvailable: boolean;
    title?: string;
    body?: string;
    subjectName?: string;
    subjectRole?: string;
    subjectStatus?: string;
    reporterName?: string;
    channelName?: string;
    parentBody?: string;
    imageIds: string[];
    historyAvailable: boolean;
    history: Array<{ action: string; actorName?: string; notes?: string; occurredAt?: string }>;
}

// ─── Invite types ────────────────────────────────────────────────────────────

export type CommunityInviteType = 'Delegate' | 'Email' | 'SecureLink' | 'CollaboratorOrganisation';
export type CommunityInviteSource = 'MembersTab' | 'BulkImport' | 'Api' | 'System';
export type CommunityInviteStatus = 'Pending' | 'Accepted' | 'Expired' | 'Revoked' | 'Consumed' | 'Declined';
export type CommunityMemberRole = 'Admin' | 'Moderator' | 'Member' | 'Guest';

export interface ICommunityInvite {
    outcome?: 'joined' | 'already_member' | 'already_channel_member';
    membershipRole?: string;
    id: string;
    communityId: string;
    channelId?: string;
    inviteType: CommunityInviteType;
    inviteSource?: CommunityInviteSource;
    recipientEmail?: string;
    recipientUserId?: string;
    externalCompanyId?: string;
    role: string;
    status: CommunityInviteStatus;
    expiresAtUtc?: string;
    maxUses?: number;
    usedCount: number;
    consumedAtUtc?: string;
    revokedAtUtc?: string;
    lastSentAtUtc?: string;
    createdAt: string;
}

export interface ICreateCommunityInvitesRequest {
    channelId?: string;
    recipientUserIds?: string[];
    recipientEmails?: string[];
    role: CommunityMemberRole;
    inviteSource?: CommunityInviteSource;
    expiresAtUtc?: string;
}

export interface ICreateCommunityInvitesResponse {
    createdCount: number;
    invites: ICommunityInvite[];
    /** Plain tokens for email invites, keyed by inviteId. Only present at creation time. */
    emailInviteTokens: Record<string, string>;
}

export interface ICreateSecureCommunityInviteRequest {
    channelId?: string;
    role: CommunityMemberRole;
    inviteSource?: CommunityInviteSource;
    expiresAtUtc?: string;
    maxUses: number;
}

export interface ICreateSecureCommunityInviteResponse {
    invite: ICommunityInvite;
    inviteToken: string;
}

export interface IGetSecureCommunityInviteTokenResponse {
    inviteToken: string;
}

// ─── Pods ────────────────────────────────────────────────────────────────────

/**
 * Pod represents a specialised community space used within events/networks.
 * Pods are communities with pod-specific configuration and preferences.
 */
export interface IPod {
    id: string;
    communityId: string;
    name: string;
    slug: string;
    description?: string;
    imageUrl?: string;
    gradient?: string;
    visibility: CommunityVisibility;
    memberCount: number;
    show_in_home: boolean;
    createdAt: string;
}

export interface IPodPreferences {
    podId: string;
    userId: string;
    show_in_home_feed: boolean;
    mute_notifications: boolean;
    updatedAt: string;
}

export interface IPodPreferencesRequest {
    show_in_home_feed?: boolean;
    mute_notifications?: boolean;
}

export interface ICreatePodRequest {
    name: string;
    invitedUserIds: string[];
}

// ─── Pod Chat ─────────────────────────────────────────────────────────────────

export interface IPodMessage {
    id: string;
    senderId: string | null;
    displayName: string | null;
    avatarUrl: string | null;
    /** 'text' | 'system' */
    type: string;
    body: string;
    sentAt: string;
}

export interface IPodResponse extends IPod {
    preferences?: IPodPreferences;
    unreadChatCount?: number;
    lastMessagePreview?: string | null;
    lastMessageAt?: string | null;
    unreadMessageCount?: number;
    muted?: boolean;
}

export interface IUpdatePodPreferencesResponse {
    preferences: IPodPreferences;
}

export interface IPodMemberResponse {
    userId: string;
    displayName: string;
    avatarUrl?: string;
    joinedAt?: string;
    invitedBy?: string;
}

export interface ICollaboratorOrganisation {
    id: string;
    communityId: string;
    externalCompanyId: string;
    externalCompanyName?: string;
    externalCompanyImageUrl?: string;
    externalCompanyEventCount?: number;
    status: string;
    seatLimit?: number;
    permissionsJson?: string;
    joinedAtUtc?: string;
    suspendedAtUtc?: string;
    removedAtUtc?: string;
}

export interface IInviteCollaboratorOrganisationRequest {
    externalCompanyId: string;
    seatLimit?: number;
    roleCeiling?: string;
}

export interface IUpdateCollaboratorOrganisationRequest {
    seatLimit?: number;
    roleCeiling?: string;
}

export interface ICollaboratorOrgInviteLink {
    invite: ICommunityInvite;
    inviteToken: string;
}

export interface ICollaboratorOrgInvitePreview {
    communityId: string;
    communityName: string;
    communitySlug: string;
    communityDescription?: string;
    invitingCompanyName: string;
    status: string;
    expiresAtUtc?: string;
}

export interface IAcceptCollaboratorOrgInviteLinkRequest {
    token: string;
}

export interface IInvitePreview {
    outcome?: string;
    membershipRole?: string;
    communityId: string;
    communitySlug: string;
    communityName: string;
    channelId?: string;
    channelName?: string;
    communityDescription?: string;
    role: string;
    inviteType: CommunityInviteType;
    status: CommunityInviteStatus;
    expiresAtUtc?: string;
    requiresEmailBinding: boolean;
    recipientEmail?: string;
    recipientEmailHasAccount?: boolean;
}

export interface IConsumeInviteRequest {
    token: string;
    expectedEmail?: string;
}
